import { useState } from "react";
import type { SubmitInput, SubmitResult } from "../api/helloAppClient.ts";
import { fileToBase64 } from "../api/helloAppClient.ts";
import { Banner } from "./Banner.tsx";

const MAX_FILE_BYTES = 25 * 1024 * 1024;

interface SubmitFormProps {
  onSubmit: (input: SubmitInput) => Promise<SubmitResult>;
  onSubmitted: () => void;
}

export function SubmitForm({ onSubmit, onSubmitted }: SubmitFormProps) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!name.trim() || !message.trim() || submitting) return;
    setError(null);

    if (file && file.size > MAX_FILE_BYTES) {
      setError(`File too large (max ${MAX_FILE_BYTES / 1024 / 1024} MB).`);
      return;
    }

    setSubmitting(true);
    try {
      const filePayload = file ? await fileToBase64(file) : undefined;
      await onSubmit({
        name: name.trim(),
        message: message.trim(),
        ...(filePayload ? { file: filePayload } : {}),
      });
      setName("");
      setMessage("");
      setFile(null);
      onSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="submit-form" noValidate>
      <div className="field">
        <label htmlFor="name">Name</label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
          required
          maxLength={200}
          placeholder="Your name"
          autoComplete="name"
        />
      </div>

      <div className="field">
        <label htmlFor="message">Message</label>
        <textarea
          id="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={submitting}
          required
          maxLength={5000}
          rows={4}
          placeholder="What would you like to share?"
        />
        <span className="field__hint">{message.length} / 5000</span>
      </div>

      <div className="field">
        <label htmlFor="file">Attachment</label>
        <input
          id="file"
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          disabled={submitting}
        />
        <span className="field__hint">Optional · up to 25 MB</span>
      </div>

      {error ? <Banner kind="error">{error}</Banner> : null}

      <div className="form-actions">
        <button
          type="submit"
          className="btn btn--primary"
          disabled={!name.trim() || !message.trim() || submitting}
        >
          {submitting ? "Submitting…" : "Submit"}
        </button>
      </div>
    </form>
  );
}
